// src/pages/materias/laboral/CrearExpedientePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "../../components/ui/notify";
import {
  Spin,
  Button,
} from "antd";
import { HappyProvider } from "@ant-design/happy-work-theme";

import FormTribunal from "../../components/forms/tribunal/FormTribunal";
import { actionTribunalGetByIdRedux, actionTribunalCreate, actionTribunalUpdateById } from "../../redux/actions/tribunal/tribunal";
import {actionConciliacionGet} from "../../redux/actions/conciliacion/conciliacion"
/* ===== helpers ===== */
const pad2 = (n) => String(n).padStart(2, "0");

const isoToDDMMYYYY = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const isoToDDMMYYYY_HHMM = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()} ${pad2(
    d.getHours()
  )}:${pad2(d.getMinutes())}`;
};

const isoToDayjs = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return dayjs(iso);
};

const extractFolioFromExpediente = (expediente) => {
  if (!expediente) return "";
  const parts = String(expediente).split("/");
  return parts.length ? parts[parts.length - 1] : String(expediente);
};

// Animación "happy work" (misma de @ant-design/happy-work-theme que usa el
// botón Guardar) aplicada a TODOS los botones de esta página vía HappyProvider.

const isoToYMD = (iso) => {
  console.log(iso)
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const backendToInitialValues = (d) => {
  if (!d) return {};

  const dg = d?.datos_generales || {};
  const ap = d?.acciones_prestaciones || {};
  const hx = d?.hechos || {};
  const ct = d?.condiciones_trabajo || {};
  const ep = d?.etapas_procesales || {};
  const pd = d?.pruebas_documentacion || {};

  // Razones sociales — convertir a string para que el Select haga match (opciones también son string)
  const rsIds = Array.isArray(dg?.razones_sociales)
    ? dg.razones_sociales.map((x) => x?.id_razon_social ? String(x.id_razon_social) : null).filter(Boolean)
    : [];

  // CITATORIO
  const citatorioArr = [];
  if (ep?.citatorio?.filename || ep?.citatorio?.url) {
    citatorioArr.push({
      id: String(ep?.citatorio?.id_tribunal_documento || ep?.id_tribunal_documento_citatorio || Date.now()),
      filename: ep?.citatorio?.filename || "documento.pdf",
      estado: "Cargado",
      url: ep?.citatorio?.url || "",
    });
  }

  const detalleActora = pd?.detalle?.actora || {};
  const checklistActora = pd?.checklist?.actora || {};

  // Confesional: el backend manda el array directamente
  const confesionalArr = Array.isArray(detalleActora?.confesional)
    ? detalleActora.confesional.map((item) => ({
        id: item?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        razon_social: item?.razon_social || undefined,
      }))
    : [];

  // Confesional hechos propios
  const rawConfesionalPropios = detalleActora?.confesional_hechos_propios;
  const confesionalPropiosArr = Array.isArray(rawConfesionalPropios)
    ? rawConfesionalPropios.map((item) => ({
        id: item?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        tipo_prueba: item?.tipo_prueba || "",
        hechos_propios: item?.hechos_propios || "",
        fecha_solicitud: isoToYMD(item?.fecha_solicitud),
      }))
    : Array.isArray(rawConfesionalPropios?.registros)
    ? rawConfesionalPropios.registros.map((item) => ({
        id: item?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        tipo_prueba: item?.tipo_prueba || "",
        hechos_propios: item?.hechos_propios || "",
        fecha_solicitud: isoToYMD(item?.fecha_solicitud),
      }))
    : [];

  // Testimonial: mapear con todos los campos (nombre, relacion, contacto)
  const testimonialTestigos = Array.isArray(detalleActora?.testimonial?.testigos)
    ? detalleActora.testimonial.testigos.map((t) => ({
        id: t?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        nombre: t?.nombre || "",
        relacion: t?.relacion || "",
        contacto: t?.contacto || "",
      }))
    : [];

  // Documental pública: convertir fechas a YYYY-MM-DD
  const docPublicaDocs = Array.isArray(detalleActora?.documental_publica?.documentos)
    ? detalleActora.documental_publica.documentos.map((doc) => ({
        id: doc?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        nombre: doc?.nombre || "",
        descripcion: doc?.descripcion || "",
        fecha: isoToYMD(doc?.fecha),
      }))
    : [];

  // Documental privada: convertir fechas a YYYY-MM-DD
  const docPrivadaDocs = Array.isArray(detalleActora?.documental_privada?.documentos)
    ? detalleActora.documental_privada.documentos.map((doc) => ({
        id: doc?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        nombre: doc?.nombre || "",
        descripcion: doc?.descripcion || "",
        fecha: isoToYMD(doc?.fecha),
      }))
    : [];

  // Informe autoridad
  const informeAutoridadDocs = Array.isArray(detalleActora?.informe_autoridad?.documentos)
    ? detalleActora.informe_autoridad.documentos.map((doc) => ({
        id: doc?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
        nombre: doc?.nombre || "",
        descripcion: doc?.descripcion || "",
        fecha: isoToYMD(doc?.fecha),
      }))
    : [];

  // Pericial: el backend puede mandar {items:[...]} con campos tipo/perito/objeto
  // El form espera array con tipo_pericial/nombre_perito/objeto_prueba
  const rawPericial = detalleActora?.pericial;
  const pericialItems = Array.isArray(rawPericial)
    ? rawPericial
    : Array.isArray(rawPericial?.items)
    ? rawPericial.items
    : Array.isArray(rawPericial?.registros)
    ? rawPericial.registros
    : [];
  const pericialArr = pericialItems.map((item) => ({
    id: item?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
    tipo_pericial: Array.isArray(item?.tipo_pericial)
      ? item.tipo_pericial
      : item?.tipo_pericial
      ? [item.tipo_pericial]
      : Array.isArray(item?.tipo)
      ? item.tipo
      : item?.tipo
      ? [item.tipo]
      : [],
    nombre_perito: item?.nombre_perito || item?.perito || "",
    objeto_prueba: item?.objeto_prueba || item?.objeto || "",
  }));

  // Inspección ocular: el backend puede mandar {items:[...]} con campos tipo/nombre/objeto
  // El form espera array con tipo_inspeccion_ocular/nombre_inspeccion_ocular/objeto_prueba
  const rawInspeccion = detalleActora?.inspeccion_ocular;
  const inspeccionItems = Array.isArray(rawInspeccion)
    ? rawInspeccion
    : Array.isArray(rawInspeccion?.items)
    ? rawInspeccion.items
    : Array.isArray(rawInspeccion?.registros)
    ? rawInspeccion.registros
    : [];
  const inspeccionArr = inspeccionItems.map((item) => ({
    id: item?.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
    tipo_inspeccion_ocular: Array.isArray(item?.tipo_inspeccion_ocular)
      ? item.tipo_inspeccion_ocular
      : item?.tipo_inspeccion_ocular
      ? [item.tipo_inspeccion_ocular]
      : Array.isArray(item?.tipo)
      ? item.tipo
      : item?.tipo
      ? [item.tipo]
      : [],
    nombre_inspeccion_ocular: item?.nombre_inspeccion_ocular || item?.nombre || "",
    objeto_prueba: item?.objeto_prueba || item?.objeto || "",
  }));

  // Medios electrónicos
  const mediosElectronicosTipos = Array.isArray(detalleActora?.medios_electronicos?.tipos)
    ? detalleActora.medios_electronicos.tipos
    : [];

  // Inferir estado del checklist desde la presencia de datos
  const inferredChecklist = {
    ...checklistActora,
    confesional: checklistActora?.confesional ?? confesionalArr.length > 0,
    confesional_hechos_propios: checklistActora?.confesional_hechos_propios ?? confesionalPropiosArr.length > 0,
    confesional_expresa: checklistActora?.confesional_expresa ?? false,
    testimonial: checklistActora?.testimonial ?? testimonialTestigos.length > 0,
    documental_publica: checklistActora?.documental_publica ?? docPublicaDocs.length > 0,
    documental_privada: checklistActora?.documental_privada ?? docPrivadaDocs.length > 0,
    informe_autoridad: checklistActora?.informe_autoridad ?? informeAutoridadDocs.length > 0,
    pericial: checklistActora?.pericial ?? pericialArr.length > 0,
    inspeccion_ocular: checklistActora?.inspeccion_ocular ?? inspeccionArr.length > 0,
    medios_electronicos: checklistActora?.medios_electronicos ?? mediosElectronicosTipos.length > 0,
    instrumental_actuaciones: checklistActora?.instrumental_actuaciones ?? true,
    presuncional_legal_humana: checklistActora?.presuncional_legal_humana ?? true,
  };

  return {
    // DATOS GENERALES
    num_unico: dg?.num_unico ?? "",
    numero_expediente: dg?.numero_expediente ?? "",
    nombre_parte_actora: ap?.trabajador_nombre ?? dg?.nombre_parte_actora ?? "",

    estado: dg?.estado ?? null,
    ciudad: dg?.ciudad ?? null,
    autoridad: dg?.autoridad ?? null,

    empresa_id: dg?.empresa_id ?? null,
    empresa_nombre: dg?.empresa_nombre ?? "",
    empresa_razon_social_ids: rsIds,

    corresponsal_nombre: dg?.corresponsal_nombre ?? "No hay corresponsal",

    // Fechas de datos generales
    fecha_radicacion_demanda: isoToYMD(dg?.fecha_radicacion),
    fecha_emision_citatorio: isoToYMD(dg?.fecha_emision_ccl),
    fecha_constancia_conciliacion: isoToYMD(dg?.fecha_no_conciliacion),
    dias_limite: dg?.dias_limite ?? null,

    // ETAPA PROCESAL
    fecha_limite_contestacion: isoToYMD(ep?.fecha_limite_contestacion),
    fecha_audiencia_conciliatoria: isoToYMD(ep?.fecha_audiencia_conciliatoria),
    observaciones_etapa_actual: ep?.observaciones_etapa_actual ?? "",

    fecha_notificacion_demanda: isoToYMD(ep?.fecha_notificacion_demanda ?? dg?.fecha_notificacion_demanda),
    fecha_presentacion_demanda: isoToYMD(ep?.fecha_presentacion_demanda ?? dg?.fecha_presentacion_demanda),
    fecha_ultimo_dia_laborado: isoToYMD(ct?.fecha_ultimo_dia_laborado ?? ep?.fecha_ultimo_dia_laborado),

    citatorio: citatorioArr,

    // ACCIONES Y PRESTACIONES
    accion_intentada: ap?.accion_intentada ?? "",
    prestaciones: { ...(ap?.prestaciones || {}) },
    otras_prestaciones: ap?.otras_prestaciones ?? "",

    // HECHOS
    responsable_despido: hx?.responsable_despido ?? "",
    jefes_inmediatos: hx?.jefes_inmediatos ?? "",
    hechos: hx?.narrativa ?? "",
    abogado_contrario: hx?.abogado_contrario ?? dg?.abogado_contrario ?? "",

    // CONDICIONES
    fecha_ingreso: isoToYMD(ct?.fecha_ingreso),
    puesto: ct?.puesto ?? "",
    salario_diario: ct?.salario_diario ?? "",
    salario_diario_integrado: ct?.salario_diario_integrado ?? dg?.salario_diario_integrado ?? "",
    jornada: ct?.jornada ?? "",
    dia_descanso: ct?.dia_descanso ?? "",
    lugar_servicio: ct?.lugar_servicio ?? "",

    // PRUEBAS
    pruebas_tab: pd?.tab === "actora" || pd?.tab === "demandada" ? pd.tab : "actora",
    pruebas_checklist: {
      actora: inferredChecklist,
    },
    pruebas_detalle: {
      actora: {
        confesional: confesionalArr,
        confesional_hechos_propios: confesionalPropiosArr,
        testimonial: {
          tipo: Array.isArray(detalleActora?.testimonial?.tipo) ? detalleActora.testimonial.tipo : [],
          draft_nombre: "",
          testigos: testimonialTestigos,
        },
        documental_publica: { documentos: docPublicaDocs },
        documental_privada: { documentos: docPrivadaDocs },
        informe_autoridad: { documentos: informeAutoridadDocs },
        pericial: pericialArr,
        inspeccion_ocular: inspeccionArr,
        medios_electronicos: {
          tipos: mediosElectronicosTipos,
          descripcion: detalleActora?.medios_electronicos?.descripcion || "",
        },
      },
    },
  };
};

export default function CrearExpedientePage(props) {
  const location = useLocation();
const [submitting, setSubmitting] = useState(false);
const [modoRapido, setModoRapido] = useState(false);

  
  const idCiudad = props?.ciudad ?? location.state?.ciudad ?? null;
  const nombreIdentificacionCiudad =
    props?.nombreIdentificacionCiudad ?? location.state?.nombreIdentificacionCiudad ?? "";

  const { idExpediente } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isEdit = Boolean(idExpediente);
  const idAutoridad = isEdit? props?.idAutoridad ?? location.state?.idAutoridad ?? null : null;

  // Datos precargados desde Centro de Conciliación (no_conciliacion)
  const prefillFromConciliacion = !isEdit ? (location.state?.prefill ?? null) : null;
  const prefillRazonSocialIds = prefillFromConciliacion?.razones_sociales_ids ?? [];
  const prefillRazonSocialNombres = prefillFromConciliacion?.razones_sociales_nombres ?? [];

  const detallesState = useSelector((state) => state?.tribunal?.detalles);
const detalle = detallesState?.detalle ?? detallesState ?? null;

  const [loadingEdit, setLoadingEdit] = useState(false);

const initialValues = useMemo(() => {
  if (isEdit) {
    if (!detalle) return {};
    return backendToInitialValues(detalle);
  }
  // Modo creación: aplicar prefill de conciliación si existe
  if (prefillFromConciliacion) {
    return {
      nombre_parte_actora: prefillFromConciliacion.nombre_parte_actora || "",
      estado: prefillFromConciliacion.estado ?? null,
      ciudad: prefillFromConciliacion.ciudad ?? null,
      num_unico: prefillFromConciliacion.num_unico || "",
      fecha_constancia_conciliacion: prefillFromConciliacion.fecha_constancia_conciliacion || "",
    };
  }
  return {};
}, [isEdit, detalle, prefillFromConciliacion]);
  useEffect(() => {
    if (!isEdit) return;

    (async () => {
      try {
        setLoadingEdit(true);
        await dispatch(actionTribunalGetByIdRedux(idExpediente));
      } catch (e) {
        notify.error({
          message: "No se pudo cargar el expediente",
          description: e?.message || "Sin detalle",
          placement: "bottomRight",
        });
      } finally {
        setLoadingEdit(false);
      }
    })();
  }, [dispatch, isEdit, idExpediente]);

 const onCancel = () => {
    navigate(`/materias/laboral/tribunal`);
  };

  const onSave = () => {
    window.dispatchEvent(new CustomEvent("oa:expediente:save"));
  };
const ready = !isEdit || !!detalle;
   return (
    <HappyProvider>
    <div className="oa-exp-page">
      {/* PORTADA (titulo grande como HTML) */}
     <section className="oa-exp-topbar">
  <div className="oa-exp-topbar-left">
    <div className="oa-exp-title">{isEdit ? "Editando Expediente de Tribunal" : "Creando Expediente de Tribunal"}</div>
    <div className="oa-exp-subtitle">Tribunal Laboral</div>
  </div>

  <div className="oa-exp-topbar-right">
    <Button className="oa-exp-btn oa-exp-btn-ghost" onClick={onCancel}>
      Cancelar
    </Button>
    <Button
      type="primary"
      className="oa-exp-btn"
      onClick={onSave}
      icon={
        <svg viewBox="0 0 448 512" width="13" height="13" fill="currentColor" aria-hidden="true">
          <path d="M64 0C28.7 0 0 28.7 0 64V448c0 35.3 28.7 64 64 64H384c35.3 0 64-28.7 64-64V154.5c0-17-6.7-33.3-18.7-45.3L400 60.7C388 48.7 371.7 42 354.5 42H64zm0 96c0-17.7 14.3-32 32-32H288c17.7 0 32 14.3 32 32v64c0 17.7-14.3 32-32 32H96c-17.7 0-32-14.3-32-32V96zM224 288a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
        </svg>
      }
    >
      Guardar expediente
    </Button>
  </div>
</section>


      {/* CONTENIDO (NO TOCAR EL FORM) */}
      <main className="oa-exp-main">
          {!ready ? (
    <div style={{ padding: 24, display: "flex", justifyContent: "center" }}>
      <Spin />
    </div>
  ) : (
        <FormTribunal
isEdit={isEdit}
      idCiudad={idCiudad}
      idExpediente={idExpediente}
      initialValues={initialValues}
      idAutoridad={idAutoridad}
      nombreIdentificacionCiudad={nombreIdentificacionCiudad}
      modoRapido={modoRapido}
      onToggleModoRapido={() => setModoRapido((v) => !v)}
      onCancel={onCancel}
      prefillRazonSocialIds={prefillRazonSocialIds}
      prefillRazonSocialNombres={prefillRazonSocialNombres}
onSubmit={async (payload, filesMap) => {
  try {
   if (isEdit) {
  const updated = await dispatch(
    actionTribunalUpdateById(idExpediente, payload, () => {}, filesMap)
  );

  notify.success({
    message: "Actualizado",
    description: "Se guardó correctamente.",
    placement: "bottomRight",
  });

  navigate(`/materias/laboral/tribunal/${idExpediente}`);
} else {
  const created = await dispatch(
    actionTribunalCreate(payload, () => {}, filesMap)
  );

  // ✅ aquí ya tienes response.data
  const newId =
    created?.id ||
    created?.data?.id ||
    created?.id_expediente ||
    created?.id_tribunal ||
    created?.insertId;

  notify.success({
    message: "Guardado",
    description: "Se guardó correctamente.",
    placement: "bottomRight",
  });

  if (newId) {
    navigate(`/materias/laboral/tribunal/${newId}`);
  } else {
    // fallback seguro si tu backend no manda id
    navigate("/materias/laboral/tribunal");
  }
}

  } catch (e) {
    const status = e?.response?.status;
    const backendMsg =
      e?.response?.data?.detail ||
      e?.response?.data?.message ||
      e?.message ||
      "Error";

    if (status === 409) {
      notify.error({
        message: "Registro duplicado",
        description: backendMsg,
        placement: "bottomRight",
      });
      return;
    }

    notify.error({
      message: "No se pudo guardar",
      description: backendMsg,
      placement: "bottomRight",
    });

    throw e;
  }
}}




/>
  )}
      </main>
    </div>
    </HappyProvider>
  );
}